// MerchantClaimHistory.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import useFetch from "../../../../hooks/useFetch";

const STATUS_STYLES = {
    Pending:         "bg-yellow-900/30 text-yellow-400 border-yellow-700/40",
    Approved:        "bg-green-900/30 text-green-400 border-green-700/40",
    PartialApproved: "bg-blue-900/30 text-blue-400 border-blue-700/40",
    Rejected:        "bg-red-900/30 text-red-400 border-red-700/40",
    Cancelled:       "bg-gray-900/30 text-gray-400 border-gray-700/40",
};

const STATUS_TABS = ["ALL", "Approved", "PartialApproved", "Rejected"];

const HistoryCard = ({ claim }) => {
    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-900 rounded-xl border border-gray-700/60 p-3 space-y-2"
        >
            <div className="flex justify-between items-start">
                <div>
                    <div className="text-sm font-bold text-white truncate">
                        🧾 {claim.billNo}
                    </div>
                    <div className="text-[11px] text-gray-400">
                        👤 {claim.customerName}
                        {claim.customerCode ? ` · ${claim.customerCode}` : ""}
                    </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                    <div className="text-green-400 font-black">₹{claim.billAmount}</div>
                    <div className="text-[10px] text-gray-500">
                        {new Date(claim.billDate).toLocaleDateString("en-IN")}
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between mt-1">
                <div className="flex items-center gap-2 text-[11px]">
                    <span className="text-gray-500">Calculated:</span>
                    <span className="text-yellow-400 font-bold">{claim.calculatedStreakPoints} pts</span>
                    {claim.approvedStreakPoints > 0 && (
                        <>
                            <span className="text-gray-600">→</span>
                            <span className="text-green-400 font-bold">{claim.approvedStreakPoints} approved</span>
                        </>
                    )}
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold ${STATUS_STYLES[claim.status] || ""}`}>
                    {claim.status}
                </span>
            </div>

            {claim.approvalRemarks && (
                <div className="text-[10px] text-gray-400 pt-1 border-t border-gray-800">
                    📝 {claim.approvalRemarks}
                </div>
            )}

            <div className="text-[10px] text-gray-600">
                Processed: {claim.approvedOn ? new Date(claim.approvedOn).toLocaleString("en-IN") : "—"}
            </div>
        </motion.div>
    );
};

const MerchantClaimHistory = () => {
    const authState = useSelector((s) => s.authReducer);
    const [fetchData] = useFetch();
    const token = authState?.token;
    const user  = authState?.user;

    const [records,  setRecords]  = useState([]);
    const [loading,  setLoading]  = useState(false);
    const [error,    setError]    = useState(null);

    const [statusFilter, setStatusFilter] = useState("ALL");
    const [customerFilter, setCustomerFilter] = useState("ALL");
    const [fromDate, setFromDate] = useState("");
    const [toDate,   setToDate]   = useState("");
    const [search,   setSearch]   = useState("");

    const loadHistory = async (statusToSend) => {
        try {
            setLoading(true);
            setError(null);

            const res = await fetchData({
                url: "/pd/streaks/claim-history-merchant",
                method: "post",
                data: {
                    shopkeeperCode: user?.think5whyscode || "",
                    ...(statusToSend && statusToSend !== "ALL" ? { status: statusToSend } : {}),

                },
                headers: { Authorization: token },
            });

            if (res?.success) {
                 const processed = (res.data || []).filter((c) => c.status !== "Pending");
                setRecords(processed);
            } else {
                setError(res?.message || "Failed to load claim history");
            }
        } catch (err) {
            setError(err.message || "Error loading claim history");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadHistory(statusFilter);
    }, [statusFilter]);

    // ── Derive customer list from loaded records (no extra API call) ──────────
    const customerOptions = useMemo(() => {
        const map = {};
        records.forEach((c) => {
            if (c.customerCode) {
                map[c.customerCode] = {
                    customerCode: c.customerCode,
                    customerName: c.customerName,
                };
            }
        });
        return Object.values(map);
    }, [records]);

    // ── Apply filters ───────────────────────────────────────────────────────
    const filtered = useMemo(() => {
        return records.filter((c) => {
            const statusMatch = statusFilter === "ALL" || c.status === statusFilter;

            const customerMatch =
                customerFilter === "ALL" || c.customerCode === customerFilter;

            const billTime = new Date(c.billDate).getTime();
            const fromMatch = !fromDate || billTime >= new Date(fromDate).setHours(0, 0, 0, 0);
            const toMatch   = !toDate   || billTime <= new Date(toDate).setHours(23, 59, 59, 999);

            const searchLower = search.trim().toLowerCase();
            const searchMatch =
                !searchLower ||
                c.billNo?.toLowerCase().includes(searchLower) ||
                c.customerName?.toLowerCase().includes(searchLower) ||
                c.customerCode?.toLowerCase().includes(searchLower);

            return statusMatch && customerMatch && fromMatch && toMatch && searchMatch;
        });
    }, [records, statusFilter, customerFilter, fromDate, toDate, search]);

    const totals = useMemo(() => {
        return {
            count: filtered.length,
            points: filtered.reduce((sum, c) => sum + (c.approvedStreakPoints || 0), 0),
        };
    }, [filtered]);

    const clearFilters = () => {
        setStatusFilter("ALL");
        setCustomerFilter("ALL");
        setFromDate("");
        setToDate("");
        setSearch("");
    };

    const hasActiveFilters =
        statusFilter !== "ALL" || customerFilter !== "ALL" || fromDate || toDate || search;

    return (
        <div className="space-y-3 bg-gray-900 p-2 rounded-xl text-white border-t-2 border-cyan-200 mt-2">

            {/* Header */}
            <div className="flex items-center justify-between px-1">
                <div className="text-cyan-300 font-bold text-sm">📜 Claim History</div>
                <button
                    onClick={() => loadHistory(statusFilter)}
                    disabled={loading}
                    className="text-[10px] w-fit px-2 py-1 rounded-lg bg-gray-800 border border-gray-700 text-gray-300 hover:border-cyan-500/50 transition-colors"
                >
                    {loading ? "Refreshing..." : "🔄 Refresh"}
                </button>
            </div>

            {/* Search */}
            <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">🔍</span>
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search bill no, customer name or code…"
                    className="w-full bg-gray-800 border border-gray-700 rounded-xl pl-9 pr-3 py-2.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 transition-colors"
                />
                {search && (
                    <button
                        onClick={() => setSearch("")}
                        className="absolute right-3 top-1/3 -translate-y-1/2 text-gray-500 hover:text-gray-300 w-fit px-2 py-1 text-xs"
                    >
                        ✕
                    </button>
                )}
            </div>

            {/* Date range */}
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="text-[10px] text-gray-400">From</label>
                    <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="w-full mt-1 bg-gray-800 border border-gray-700 rounded-xl px-2 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
                    />
                </div>
                <div>
                    <label className="text-[10px] text-gray-400">To</label>
                    <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="w-full mt-1 bg-gray-800 border border-gray-700 rounded-xl px-2 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500/50"
                    />
                </div>
            </div>

            {/* Customer filter */}
            {customerOptions.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={() => setCustomerFilter("ALL")}
                        className={`w-fit px-2 py-1 text-[10px] rounded-md border transition-colors ${
                            customerFilter === "ALL"
                                ? "bg-cyan-500 border-cyan-500 text-black font-bold"
                                : "bg-gray-700 border-gray-600 text-gray-200"
                        }`}
                    >
                        All Customers
                    </button>
                    {customerOptions.map((cust) => (
                        <button
                            key={cust.customerCode}
                            onClick={() => setCustomerFilter(cust.customerCode)}
                            className={`w-fit px-2 py-1 text-[10px] rounded-md border transition-colors ${
                                customerFilter === cust.customerCode
                                    ? "bg-cyan-500 border-cyan-500 text-black font-bold"
                                    : "bg-gray-700 border-gray-600 text-gray-200"
                            }`}
                        >
                            {cust.customerName || cust.customerCode}
                        </button>
                    ))}
                </div>
            )}

            {/* Status filter */}
            <div className="flex gap-2 flex-wrap">
                {STATUS_TABS.map((s) => (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`w-24 px-2 py-0.5 text-[10px] rounded-md border transition-colors ${
                            statusFilter === s
                                ? "bg-gray-200 border-gray-200 text-black font-bold"
                                : "bg-gray-700 border-gray-600 text-gray-200"
                        }`}
                    >
                        {s}
                    </button>
                ))}
            </div>

            {hasActiveFilters && (
                <button
                    onClick={clearFilters}
                    className="text-[10px] w-fit px-2 py-1 rounded-md bg-cyan-600 text-white"
                >
                    Clear all filters
                </button>
            )}

            {/* Summary row */}
            <div className="flex justify-between items-center text-xs text-gray-400 px-1">
                <span>Showing: <strong className="text-white">{totals.count}</strong></span>
                <span>Total Approved: <strong className="text-green-400">{totals.points} pts</strong></span>
            </div>

            {/* List */}
            {loading ? (
                <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-gray-900 border border-gray-800 rounded-xl p-4 animate-pulse">
                            <div className="h-3 bg-gray-800 rounded w-1/2 mb-2" />
                            <div className="h-3 bg-gray-800 rounded w-1/3" />
                        </div>
                    ))}
                </div>
            ) : error ? (
                <div className="bg-red-900/30 border border-red-700/40 rounded-xl p-4 text-center text-red-300 text-xs">
                    ⚠️ {error}
                </div>
            ) : filtered.length === 0 ? (
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 text-center">
                    <div className="text-3xl mb-2">📭</div>
                    <div className="text-gray-400 text-sm">
                        {records.length === 0 ? "No processed claims yet" : "No claims match your filters"}
                    </div>
                    {records.length > 0 && hasActiveFilters && (
                        <button
                            onClick={clearFilters}
                            className="mt-2 text-xs text-cyan-400"
                        >
                            Clear filters
                        </button>
                    )}
                </div>
            ) : (
                <div className="space-y-2">
                    <AnimatePresence>
                        {filtered.map((claim) => (
                            <HistoryCard key={claim._id} claim={claim} />
                        ))}
                    </AnimatePresence>
                </div>
            )}
        </div>
    );
};

export default MerchantClaimHistory;